import { ERROR_CODES } from '@garage/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../../shared/api.js';
import { applyBrand } from '../../shared/theme.js';
import { buttonClasses } from '../../ui/Button.js';
import { PortalView } from '../components/PortalView.js';
import { PORTAL_QUERY_KEY } from '../keys.js';
import { getPortalToken, initPortalToken } from '../token.js';

/**
 * Portail client, ouvert par le lien reçu (`/p#<jeton>`). Sans compte. La marque affichée est
 * celle renvoyée par l'API pour ce dossier ; rien n'est propre à un garage dans le code.
 */
export default function PortalPage() {
  const qc = useQueryClient();
  const [token, setToken] = useState(() => initPortalToken());

  // Un nouveau lien ouvert dans le même onglet ne change que le fragment : on oublie l'ancien dossier.
  useEffect(() => {
    const onHash = () => {
      const previous = getPortalToken();
      const next = initPortalToken();
      if (next === previous) return;
      setToken(next);
      void qc.resetQueries({ queryKey: PORTAL_QUERY_KEY });
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [qc]);

  const view = useQuery({
    queryKey: PORTAL_QUERY_KEY,
    queryFn: () => api('portal.view'),
    enabled: !!token,
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
  });

  const garage = view.data?.garage ?? null;
  useEffect(() => applyBrand(garage), [garage]);

  if (!token)
    return <Message title="Lien incomplet">Ouvrez à nouveau le lien reçu par e-mail ou par message.</Message>;
  if (view.isPending) {
    return (
      <div
        role="status"
        className="flex min-h-dvh items-center justify-center gap-3 text-sm font-medium text-ink-3"
      >
        <span
          aria-hidden
          className="size-5 animate-spin rounded-full border-2 border-line-strong border-t-ink-3"
        />
        Chargement…
      </div>
    );
  }
  if (view.isError) {
    // C2 : garage momentanément indisponible (403 `portal_unavailable`) : texte neutre, le lien
    // refonctionnera ; ne pas inviter le client à demander un nouveau lien.
    if (view.error instanceof ApiError && view.error.code === ERROR_CODES.portalUnavailable) {
      return (
        <Message title="Suivi momentanément indisponible" retry={() => void view.refetch()}>
          Le suivi de votre véhicule n’est pas accessible pour le moment. Votre lien reste le bon : réessayez plus tard.
        </Message>
      );
    }
    const expired = view.error instanceof ApiError && view.error.status < 500;
    return expired ? (
      <Message title="Ce lien n’est plus valable">
        Il a expiré ou a été remplacé. Demandez au garage de vous envoyer un nouveau lien.
      </Message>
    ) : (
      <Message title="Page momentanément indisponible" retry={() => void view.refetch()}>
        Vérifiez votre connexion puis réessayez.
      </Message>
    );
  }
  return <PortalView view={view.data} />;
}

function Message({ title, children, retry }: { title: string; children: string; retry?: () => void }) {
  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 text-center">
      <h1 className="text-2xl font-semibold tracking-[-0.02em] text-ink">{title}</h1>
      <p className="mt-2 text-ink-2">{children}</p>
      {retry ? (
        <button
          type="button"
          onClick={retry}
          className={buttonClasses({ variant: 'secondary', className: 'mx-auto mt-6 px-6' })}
        >
          Réessayer
        </button>
      ) : null}
    </div>
  );
}
